import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Boxes,
  Users,
  Hammer,
  Inbox,
  ShieldAlert,
  CalendarCheck,
  Receipt,
  LogOut,
  ExternalLink,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true, testid: "nav-overview" },
  { to: "/admin/inventory", icon: Boxes, label: "Inventory", testid: "nav-inventory" },
  { to: "/admin/employees", icon: Users, label: "Employees", testid: "nav-employees" },
  { to: "/admin/attendance", icon: CalendarCheck, label: "Attendance", testid: "nav-attendance" },
  { to: "/admin/projects", icon: Hammer, label: "Projects", testid: "nav-projects" },
  { to: "/admin/invoices", icon: Receipt, label: "Invoices", testid: "nav-invoices" },
  { to: "/admin/compliance", icon: ShieldAlert, label: "Compliance", testid: "nav-compliance" },
  { to: "/admin/requests", icon: Inbox, label: "Requests", testid: "nav-requests" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-muted/30 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-72 shrink-0 flex-col bg-secondary text-secondary-foreground">
        <div className="px-7 py-7 border-b border-white/10">
          <Logo light />
        </div>
        <nav className="flex-1 px-4 py-6 space-y-1">
          {navItems.map(({ to, icon: Icon, label, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm rounded-sm transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-secondary-foreground/70 hover:text-secondary-foreground hover:bg-white/5"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-3">
          <Link
            to="/"
            className="flex items-center justify-between px-4 py-3 text-xs uppercase tracking-[0.2em] text-secondary-foreground/60 hover:text-primary"
            data-testid="admin-view-site"
          >
            View public site
            <ExternalLink className="h-3 w-3" />
          </Link>
          <div className="px-4 py-3 bg-white/5 rounded-sm">
            <div className="text-xs text-secondary-foreground/60">Signed in as</div>
            <div className="text-sm font-semibold truncate">{user?.email}</div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-secondary-foreground/70 hover:text-white hover:bg-white/10"
            data-testid="admin-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-secondary text-secondary-foreground border-b border-white/10">
        <div className="flex items-center justify-between px-4 h-16">
          <Logo light size={28} />
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex overflow-x-auto px-2 pb-2 gap-1">
          {navItems.map(({ to, label, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`mobile-${testid}`}
              className={({ isActive }) =>
                `px-3 py-2 text-xs whitespace-nowrap rounded-sm ${
                  isActive ? "bg-primary text-primary-foreground" : "text-white/70"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 lg:pl-0 pt-28 lg:pt-0 pb-16">
        <div className="px-6 sm:px-10 py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
